import React, { useState } from 'react';
import { Button, Skeleton, Table } from 'antd';
import { Link } from 'react-router-dom';
import { sortBy, uniqBy } from 'lodash';
import { useDsps } from '../../../hooks/useDsps';
import { PageLayout } from '../../../components/PageLayout';
import { Search } from '../ssps/Search';

export function DspsV2Page() {
  const { dsps, isBaseLoaded, isLastHourStatsLoaded, isSpendLoaded } = useDsps();

  const [searchQuery, setSearchQuery] = useState();

  const getBidQps = (lastHourStatistics) =>
    lastHourStatistics ? Math.ceil(lastHourStatistics.dspBidResponseValid / 3600) : 0;

  const getReqQps = (lastHourStatistics) =>
    lastHourStatistics ? Math.ceil(lastHourStatistics.dspBidRequest / 3600) : 0;

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      fixed: 'left',
      render: (name, { id }) => (
        <Link to={`/dsps/${id}`}>
          [{id}] {name}
        </Link>
      ),
    },
    {
      title: 'Company',
      dataIndex: 'company.firstName',
      key: 'company.firstName',
      render: (value, record) => record.company.firstName,
      filters: uniqBy(
        sortBy(
          (dsps || []).map((dsp) => dsp.company),
          'firstName'
        ),
        'id'
      ).map((company) => ({
        text: company.firstName,
        value: company.id,
      })),
      onFilter: (value, { company }) => company.id === value,
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive) => (isActive ? 'Active' : 'Inactive'),
      filters: [
        {
          text: 'Active',
          value: 1,
        },
        {
          text: 'Inactive',
          value: 0,
        },
      ],
      onFilter: (value, { isActive }) => isActive === value,
    },

    {
      title: 'Traffic Type',
      key: 'trafficType',
      dataIndex: 'trafficType',
      render: (value, { useBanner, useVideo, useNative }) =>
        [useBanner && 'Banner', useVideo && 'Video', useNative && 'Native'].filter(Boolean).join(', ') || 'None',
      filters: [
        {
          text: 'Banner',
          value: 'banner',
        },
        {
          text: 'Video',
          value: 'video',
        },
        {
          text: 'Native',
          value: 'native',
        },
      ],
      onFilter: (value, { useBanner, useVideo, useNative }) => {
        if (value === 'banner') {
          return useBanner;
        }

        if (value === 'video') {
          return useVideo;
        }

        if (value === 'native') {
          return useNative;
        }

        return false;
      },
    },
    {
      title: 'Real QPS',
      key: 'realQps',
      dataIndex: 'realQps',
      render: (value, { lastHourStatistics, limitQps }) =>
        isLastHourStatsLoaded ? (
          `${lastHourStatistics?.dspBidRequest ? Math.round(lastHourStatistics.dspBidRequest / 3600) : 0} / ${
            limitQps || 0
          }`
        ) : (
          <Skeleton.Input active size="small" style={{ width: 70 }} />
        ),
      sorter: (a, b) => getReqQps(a.lastHourStatistics) - getReqQps(b.lastHourStatistics),
    },
    {
      title: 'Bid QPS',
      key: 'bidQps',
      dataIndex: 'bidQps',
      render: (value, { lastHourStatistics }) =>
        isLastHourStatsLoaded ? (
          lastHourStatistics?.dspBidResponseValid ? (
            Math.round(lastHourStatistics.dspBidResponseValid / 3600)
          ) : (
            0
          )
        ) : (
          <Skeleton.Input active size="small" style={{ width: 55 }} />
        ),
      defaultSortOrder: 'descend',
      sorter: (a, b) => getBidQps(a.lastHourStatistics) - getBidQps(b.lastHourStatistics),
    },
    {
      title: 'Margin %',
      dataIndex: 'margin',
      key: 'margin',
      render: (value) => value || 0,
      sorter: (a, b) => a.margin - b.margin,
    },
    {
      title: 'Rev. Share %',
      dataIndex: 'extraMargin',
      key: 'extraMargin',
      render: (value) => value || 0,
      sorter: (a, b) => a.extraMargin - b.extraMargin,
    },
    {
      title: 'Spend Today',
      dataIndex: 'dailySpend',
      key: 'dailySpend',
      // @ts-expect-error TODO
      render: (value, record) =>
        isSpendLoaded ? `${value || 0} / ${record.dailySpendLimit || 0}` : <Skeleton.Input active size="small" />,
      // @ts-expect-error TODO
      sorter: (a, b) => (a.dailySpend || 0) - (b.dailySpend || 0),
    },
    {
      title: 'Spend Total',
      dataIndex: 'totalSpend',
      key: 'totalSpend',
      // @ts-expect-error TODO
      render: (value, record) =>
        isSpendLoaded ? `${value || 0} / ${record.totalSpendLimit || 0}` : <Skeleton.Input active size="small" />,
      // @ts-expect-error TODO
      sorter: (a, b) => (a.totalSpend || 0) - (b.totalSpend || 0),
    },
    {
      title: 'Req',
      key: 'req',
      dataIndex: 'req',
      render: (value, { lastHourStatistics }) =>
        isLastHourStatsLoaded ? (
          (lastHourStatistics?.dspBidRequest || 0).toLocaleString()
        ) : (
          <Skeleton.Input active size="small" style={{ width: 60 }} />
        ),
      sorter: (a, b) => (a.lastHourStatistics?.dspBidRequest || 0) - (b.lastHourStatistics?.dspBidRequest || 0),
    },
    {
      title: 'Res',
      key: 'res',
      dataIndex: 'res',
      render: (value, { lastHourStatistics }) =>
        isLastHourStatsLoaded ? (
          (lastHourStatistics?.dspBidResponseValid || 0).toLocaleString()
        ) : (
          <Skeleton.Input active size="small" style={{ width: 60 }} />
        ),
      sorter: (a, b) =>
        (a.lastHourStatistics?.dspBidResponseValid || 0) - (b.lastHourStatistics?.dspBidResponseValid || 0),
    },
    {
      title: 'Optimizer',
      dataIndex: 'optimizer',
      key: 'optimizer',
      render: (isActive) => (isActive ? 'Active' : 'Inactive'),
      filters: [
        {
          text: 'Active',
          value: 1,
        },
        {
          text: 'Inactive',
          value: 0,
        },
      ],
      onFilter: (value, { optimizer }) => optimizer === value,
    },
    {
      title: 'Resolve DNS',
      dataIndex: 'dnsResolve',
      key: 'dnsResolve',
      render: (isActive) => (isActive ? 'Active' : 'Inactive'),
      filters: [
        {
          text: 'Active',
          value: 1,
        },
        {
          text: 'Inactive',
          value: 0,
        },
      ],
      onFilter: (value, { dnsResolve }) => dnsResolve === value,
    },
    {
      title: 'Gzip',
      dataIndex: 'useGzip',
      key: 'useGzip',
      render: (isActive) => (isActive ? 'Active' : 'Inactive'),
      filters: [
        {
          text: 'Active',
          value: 1,
        },
        {
          text: 'Inactive',
          value: 0,
        },
      ],
      onFilter: (value, { useGzip }) => useGzip === value,
    },
    {
      title: 'No Imp Bidding',
      dataIndex: 'noImpBidding',
      key: 'noImpBidding',
      render: (isActive) => (isActive ? 'Active' : 'Inactive'),
      filters: [
        {
          text: 'Active',
          value: 1,
        },
        {
          text: 'Inactive',
          value: 0,
        },
      ],
      onFilter: (value, { noImpBidding }) => noImpBidding === value,
    },
  ].map((col) => {
    return {
      width: 100,
      ...col,
    };
  });

  const sspsToDisplay = searchQuery
    ? dsps.filter(({ id, name }) => `[${id}] ${name}`.toLowerCase().includes(searchQuery.toLowerCase()))
    : dsps;

  return (
    <PageLayout>
      <Link to="/dsps/new">
        <Button>Create new DSP</Button>
      </Link>

      <Search onSearch={setSearchQuery} />

      <Table
        columns={columns}
        dataSource={sspsToDisplay}
        pagination={false}
        size="small"
        loading={!isBaseLoaded}
        scroll={{ x: 'max-content', y: 'calc(100vh - 300px)' }}
      />
    </PageLayout>
  );
}
